import { AutomationType, CandidateBatch } from 'generated-graphql/types';
import { ToastType, useToast } from 'services/toast';
import { useConfirmationModal } from 'services/modals/hooks';
import { useTracking } from 'services/tracking/hooks';
import { useDrawer } from 'services/drawer/hooks';
import { useState } from 'react';
import { AnalysisAutomationItem } from 'modules/analyse/types/AutomationTasks';
import { useGetAutomationTasksByAutomationTypeQuery } from 'generated-graphql/hooks/apollo-hooks';
import { useFormik } from 'formik';
import { validateAnalysisAutomationTasks } from 'modules/analyse/utils/validateAutomationTasks';
import { normalizeBetweenScoreConditions } from 'modules/analyse/utils/normalizeBetweenScoreConditions';
import { formatAutomationTasksForSubmission } from 'modules/analyse/utils/formatAutomationTasksForSubmission';
import { handleAnalyseAutomationTracking } from 'modules/analyse/utils/handleAnalyseAutomationTracking';
import { extractListItems } from 'core/utils/apollo/extractListItems';
import { FailedMessageWithHref } from 'common/ui';
import { actionsValidationSchema } from 'modules/analyse/constants/actionsValidationsSchema';
import { AutomationFormItem } from 'modules/analyse/types/AutomationFormItem';
import { useAnalyseUpdateCandidateBatchGroup } from 'modules/analyse/hooks/useAnalyseUpdateCandidateBatchGroup';
import { useAnalyseUpdateAutomationTasks } from 'modules/analyse/hooks/useAnalyseUpdateAutomationTasks';

type FormValues = {
  automationTasks: AnalysisAutomationItem[];
  overwriteLockedCandidates: boolean;
  saveOnly: boolean;
};

type UseAnalysisAutomationTasksProps = {
  candidateBatch: CandidateBatch;
  setCandidateBatch: (candidateBatch: CandidateBatch) => void;
  automationType: AutomationType;
};

export const useAnalysisAutomationTasks = ({
  candidateBatch,
  setCandidateBatch,
  automationType
}: UseAnalysisAutomationTasksProps) => {
  const candidateBatchId = candidateBatch.id;
  const candidateBatchGroupId = candidateBatch.candidateBatchGroupId;

  const { showToast } = useToast();
  const { showModal } = useConfirmationModal();
  const { track } = useTracking();
  const { updateDrawerComponentProps, updateDrawerProps } = useDrawer();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [initialAutomationTasks, setInitialAutomationTasks] = useState<
    AnalysisAutomationItem[]
  >([]);

  const detachCandidateBatchGroup = () => {
    setCandidateBatch({
      ...candidateBatch,
      candidateBatchGroupId: null
    });

    updateDrawerComponentProps({
      candidateBatch: {
        ...candidateBatch,
        candidateBatchGroupId: null
      }
    });
  };

  const { submitForm: submitFormForGroup } =
    useAnalyseUpdateCandidateBatchGroup({
      automationType,
      initialAutomationTasks,
      candidateBatchGroupName: candidateBatch.candidateBatchGroup?.name
    });

  const { submitForm: submitFormForAutomationTasks } =
    useAnalyseUpdateAutomationTasks({
      automationType,
      candidateBatchId
    });

  const formik = useFormik<FormValues>({
    initialValues: {
      automationTasks: [],
      overwriteLockedCandidates: false,
      saveOnly: false
    },
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: actionsValidationSchema(),
    onSubmit: async (values) => {
      const normalizedTasks = normalizeBetweenScoreConditions(
        values.automationTasks || []
      );

      const { isAllTasksValid, isDuplicates } =
        validateAnalysisAutomationTasks(normalizedTasks);

      if (!isAllTasksValid) {
        showToast({
          message: 'You have some invalid automation tasks',
          type: ToastType.Error
        });
        return;
      }

      if (isDuplicates) {
        const errorTitle =
          automationType === AutomationType.Rule
            ? 'Review and update rules'
            : 'Review and update actions';

        showToast({
          title: errorTitle,
          message: (
            <FailedMessageWithHref
              failedMessage="They conflict with each other and must be adjusted."
              emailSubject="Failed create automations due to duplicates"
            />
          ),
          type: ToastType.Error
        });
        return;
      }

      const automationTasksActions = formatAutomationTasksForSubmission(
        normalizedTasks,
        initialAutomationTasks || []
      );

      handleAnalyseAutomationTracking({
        automationTasksActions,
        track
      });

      if (candidateBatchGroupId) {
        showModal({
          title: 'Save rules',
          message:
            'Do you want to save these settings for this analysis, or for all analyses that use this Ruleset?',
          onConfirm: async () => {
            try {
              setIsSaving(values.saveOnly);
              setIsSubmitting(!values.saveOnly);

              await submitFormForAutomationTasks({
                automationTasksActions,
                overwriteLockedCandidates: values.overwriteLockedCandidates,
                saveOnly: values.saveOnly
              });
            } finally {
              setIsSubmitting(false);
              setIsSaving(false);
            }

            // Detach the candidateBatchGroupId from this analysis so on next save it will be saved only for this analysis
            detachCandidateBatchGroup();
          },
          confirmationText: 'Just this',
          showSecondaryButton: true,
          secondaryText: 'All',
          onSecondaryClick: async () => {
            try {
              setIsSaving(values.saveOnly);
              setIsSubmitting(!values.saveOnly);

              await submitFormForGroup({
                candidateBatchGroupId,
                automationTasksActions,
                overwriteLockedCandidates: values.overwriteLockedCandidates,
                saveOnly: values.saveOnly
              });
            } finally {
              setIsSubmitting(false);
              setIsSaving(false);
            }
          }
        });

        return;
      }

      try {
        setIsSaving(values.saveOnly);
        setIsSubmitting(!values.saveOnly);

        await submitFormForAutomationTasks({
          automationTasksActions,
          overwriteLockedCandidates: values.overwriteLockedCandidates,
          saveOnly: values.saveOnly
        });
      } finally {
        setIsSubmitting(false);
        setIsSaving(false);
      }
    },
    enableReinitialize: true
  });

  const { loading } = useGetAutomationTasksByAutomationTypeQuery({
    variables: {
      candidateBatchId,
      automationType
    },
    onCompleted: (data) => {
      const result = extractListItems(
        data.getAutomationTasksByAutomationType?.automationTasks || []
      );

      setInitialAutomationTasks(result);
      formik.setFieldValue('automationTasks', result);
    }
  });

  const onFormChanged = (changedAutomationTasks: AutomationFormItem[]) => {
    const {
      automationTasksToCreate,
      automationTasksToDelete,
      automationTasksToUpdate
    } = formatAutomationTasksForSubmission(
      changedAutomationTasks || [],
      initialAutomationTasks || []
    );

    if (
      automationTasksToCreate.length > 0 ||
      automationTasksToDelete.length > 0 ||
      automationTasksToUpdate.length > 0
    ) {
      updateDrawerProps({
        preventClose: true,
        confirmationModalProps: {
          title: 'Save changes?',
          confirmationText: 'Confirm',
          cancelText: 'Cancel',
          onConfirm: async () => {
            await formik.submitForm();
          }
        }
      });
    } else {
      updateDrawerProps({
        preventClose: false
      });
    }
  };

  return {
    formik,
    loading,
    onFormChanged,
    isSubmitting,
    isSaving
  };
};
